
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import commonService from '@/services/common-services';
import orderService from '@/services/order-service';
import { APP_CONFIG } from '@/config/app-config';
import formatNumber from '@/shared/filters/format-number';

interface ProductData {
    title_origin: string;
    property: string;
    link_origin: string;
    price_origin: number;
    quantity: number;
    imageFile?: File | null;
    imagePreview?: string;
    imageUrl?: string;
}

interface OrderData {
    storeCNID: number | string;
    storeVNID: number | string;
    shopName: string;
    note: string;
    products: ProductData[];
}

@Component
export default class CreateManualOrderModal extends Vue {
    @Prop({ default: false })
    private isShow!: boolean;

    private isSubmitting = false;
    private orderData: OrderData = {
        storeCNID: '',
        storeVNID: '',
        shopName: '',
        note: '',
        products: [this.createEmptyProduct()]
    };

    get warehouses() {
        return this.$store.state[APP_CONFIG.stateKey.commonStore].warehouse || [];
    }

    get userInfo() {
        return this.$store.state[APP_CONFIG.stateKey.authStore].userInfo;
    }

    @Watch('isShow')
    private onShowChanged(val: boolean) {
        if (val) {
            this.resetForm();
            this.loadWarehouses();
        }
    }

    private async loadWarehouses() {
        if (!this.warehouses || this.warehouses.length === 0) {
            const result = await commonService.getWarehouse();
            if (result && result.status === 200) {
                (window['getApp'] as any).$emit('SET_WAREHOUSE', result.data.data);
            }
        }
    }

    private createEmptyProduct(): ProductData {
        return {
            title_origin: '',
            property: '',
            link_origin: '',
            price_origin: 0,
            quantity: 1,
            imageFile: null,
            imagePreview: '',
            imageUrl: ''
        };
    }

    private addProduct() {
        this.orderData.products.push(this.createEmptyProduct());
    }

    private removeProduct(index: number) {
        this.orderData.products.splice(index, 1);
    }

    private triggerFileInput(index: number) {
        const fileInput = (this.$refs['fileInput' + index] as any)[0];
        if (fileInput) {
            fileInput.click();
        }
    }

    private onFileChange(event: any, index: number) {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            this.orderData.products[index].imageFile = file;
            
            // Create preview
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.orderData.products[index].imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        } else {
            Vue.notify({
                group: 'foo',
                type: 'warn',
                title: 'Thông báo!',
                text: 'Vui lòng chọn file ảnh hợp lệ!'
            });
        }
    }

    private removeImage(index: number) {
        this.orderData.products[index].imageFile = null;
        this.orderData.products[index].imagePreview = '';
        this.orderData.products[index].imageUrl = '';
        
        // Reset file input
        const fileInput = (this.$refs['fileInput' + index] as any)[0];
        if (fileInput) {
            fileInput.value = '';
        }
    }

    private calculateProductTotal(product: ProductData): string {
        const total = product.price_origin * product.quantity;
        return formatNumber(total, '0,0.00');
    }

    private get totalAmount(): number {
        return this.orderData.products.reduce((sum, product) => {
            return sum + (product.price_origin * product.quantity);
        }, 0);
    }

    private formatNumber(value: number): string {
        return formatNumber(value, '0,0.00');
    }

    private validateForm(): boolean {
        for (let i = 0; i < this.orderData.products.length; i++) {
            const product = this.orderData.products[i];
            
            if (!product.title_origin || product.title_origin.trim() === '') {
                Vue.notify({
                    group: 'foo',
                    type: 'warn',
                    title: 'Thông báo!',
                    text: `Vui lòng nhập tên sản phẩm ${i + 1}!`
                });
                return false;
            }

            if (!product.price_origin || product.price_origin <= 0) {
                Vue.notify({
                    group: 'foo',
                    type: 'warn',
                    title: 'Thông báo!',
                    text: `Vui lòng nhập đơn giá cho sản phẩm ${i + 1}!`
                });
                return false;
            }

            if (!product.quantity || product.quantity <= 0) {
                Vue.notify({
                    group: 'foo',
                    type: 'warn',
                    title: 'Thông báo!',
                    text: `Vui lòng nhập số lượng cho sản phẩm ${i + 1}!`
                });
                return false;
            }
        }

        return true;
    }

    private async uploadImage(file: File): Promise<string | null> {
        try {
            const formData = new FormData();
            const timestamp = new Date().getTime();
            const filename = `manual_order_${timestamp}_${file.name}`;
            
            formData.append('imagefile', file);
            formData.append('filename', filename);

            const result = await orderService.uploadImage(formData);
            
            if (result && result.status === 200 && result.data.data) {
                return result.data.data;
            }
            
            return null;
        } catch (error) {
            // Upload image error
            return null;
        }
    }

    private async onSubmit() {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            // Upload images first
            for (const product of this.orderData.products) {
                if (product.imageFile) {
                    const imageUrl = await this.uploadImage(product.imageFile);
                    if (imageUrl) {
                        product.imageUrl = imageUrl;
                    }
                }
            }

            // Prepare order data
            const orderPayload = {
                // StoreCNID: this.orderData.storeCNID,
                // StoreVNID: this.orderData.storeVNID,
                ShopName: this.orderData.shopName || 'Đơn thủ công',
                Site: 'manual',
                IsWeb: 0,
                Status: 0,
                Products: this.orderData.products.map(product => ({
                    title_origin: product.title_origin,
                    property: product.property,
                    link_origin: product.link_origin,
                    price_origin: product.price_origin,
                    quantity: product.quantity,
                    image_origin: product.imageUrl || ''
                }))
            };

            const result = await orderService.createManualOrder(orderPayload);

            if (result && result.status === 200 && result.data.status === 200) {
                Vue.notify({
                    group: 'foo',
                    type: 'success',
                    title: 'Thông báo!',
                    text: 'Tạo đơn hàng thủ công thành công!'
                });
                
                this.$emit('eventOrderCreated');
                this.onClose();
            } else {
                Vue.notify({
                    group: 'foo',
                    type: 'error',
                    title: 'Lỗi!',
                    text: (result && result.data && result.data.reason) || 'Không thể tạo đơn hàng. Vui lòng thử lại!'
                });
            }
        } catch (error) {
            // console.error('Create manual order error:', error);
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Lỗi!',
                text: 'Có lỗi xảy ra khi tạo đơn hàng!'
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    private resetForm() {
        this.orderData = {
            storeCNID: '',
            storeVNID: '',
            shopName: '',
            note: '',
            products: [this.createEmptyProduct()]
        };
        this.isSubmitting = false;
    }

    private onClose() {
        this.$emit('eventCloseModal');
    }
}
